/**
 * Utility functions for handling image URLs and ensuring they work across network devices
 * Enhanced to support Firebase Storage URLs with format and size variants
 */
import { getApiUrl } from './apiUrlResolver';

// Get the API base URL from dynamic resolver
const getApiBaseUrl = (): string => {
  return getApiUrl().replace('/api', '');
};

/**
 * Check if URL is a Firebase Storage URL
 */
const isFirebaseStorageUrl = (url: string): boolean => {
  return url.includes('firebasestorage.googleapis.com') || url.includes('firebase.google.com');
};

/**
 * Resolves relative image URLs to absolute URLs with Firebase Storage support
 * This ensures images work when accessing the app from other devices on the network
 */
export const resolveImageUrl = (imageUrl: string | undefined): string => {
  if (!imageUrl) {
    return getFallbackImageUrl(); // Return brand logo as placeholder
  }

  // If the URL is already absolute (including Firebase Storage), return it as-is
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    return imageUrl;
  }

  // If it's a relative URL (starts with /), prepend the API base URL
  if (imageUrl.startsWith('/')) {
    return `${getApiBaseUrl()}${imageUrl}`;
  }

  // Otherwise, assume it's a relative path and add the API base URL with a slash
  return `${getApiBaseUrl()}/${imageUrl}`;
};

/**
 * Resolves image source objects (with avif, webp, jpeg formats) to use absolute URLs
 * Enhanced to handle Firebase Storage URLs
 */
export const resolveImageSource = (
  imageSource:
    | string
    | {
        avif?: string;
        webp?: string;
        jpeg: string;
      }
    | null
):
  | string
  | {
      avif?: string;
      webp?: string;
      jpeg: string;
    }
  | null => {
  if (!imageSource) {
    return null;
  }

  // If it's a string, resolve it directly
  if (typeof imageSource === 'string') {
    return resolveImageUrl(imageSource);
  }

  // If it's an object with multiple formats, resolve each one
  return {
    avif: imageSource.avif ? resolveImageUrl(imageSource.avif) : undefined,
    webp: imageSource.webp ? resolveImageUrl(imageSource.webp) : undefined,
    jpeg: resolveImageUrl(imageSource.jpeg),
  };
};

/**
 * Resolves thumbnail objects to use absolute URLs
 * Enhanced to handle Firebase Storage thumbnail structure
 */
export const resolveThumbnailSource = (
  thumbnail:
    | {
        avif?: string;
        webp?: string;
        jpeg: string;
      }
    | undefined
):
  | {
      avif?: string;
      webp?: string;
      jpeg: string;
    }
  | undefined => {
  if (!thumbnail) {
    return undefined;
  }

  return {
    avif: thumbnail.avif ? resolveImageUrl(thumbnail.avif) : undefined,
    webp: thumbnail.webp ? resolveImageUrl(thumbnail.webp) : undefined,
    jpeg: resolveImageUrl(thumbnail.jpeg),
  };
};

/**
 * NEW: Generate srcSet for responsive images with Firebase Storage support
 */
export const generateSrcSet = (
  imageSource: {
    avif?: string;
    webp?: string;
    jpeg: string;
    thumbnails?: {
      small: { avif?: string; webp?: string; jpeg: string };
      medium: { avif?: string; webp?: string; jpeg: string };
      large: { avif?: string; webp?: string; jpeg: string };
    };
  },
  format: 'avif' | 'webp' | 'jpeg' = 'jpeg'
): string => {
  const srcSetEntries: string[] = [];

  // Add thumbnail sizes if available
  if (imageSource.thumbnails) {
    const { small, medium, large } = imageSource.thumbnails;

    if (small[format]) {
      srcSetEntries.push(`${resolveImageUrl(small[format])} 200w`);
    }
    if (medium[format]) {
      srcSetEntries.push(`${resolveImageUrl(medium[format])} 400w`);
    }
    if (large[format]) {
      srcSetEntries.push(`${resolveImageUrl(large[format])} 600w`);
    }
  }

  // Add main image
  if (imageSource[format]) {
    srcSetEntries.push(`${resolveImageUrl(imageSource[format])} 1200w`);
  }

  return srcSetEntries.join(', ');
};

/**
 * NEW: Get optimal image source based on browser capabilities and viewport
 */
export const getOptimalImageSource = (
  imageSource: {
    avif?: string;
    webp?: string;
    jpeg: string;
    thumbnails?: {
      small: { avif?: string; webp?: string; jpeg: string };
      medium: { avif?: string; webp?: string; jpeg: string };
      large: { avif?: string; webp?: string; jpeg: string };
    };
  },
  options: {
    preferredFormat?: 'avif' | 'webp' | 'jpeg';
    preferredSize?: 'small' | 'medium' | 'large' | 'full';
    fallbackToJpeg?: boolean;
  } = {}
): string => {
  const { preferredFormat = 'jpeg', preferredSize = 'full', fallbackToJpeg = true } = options;

  // Try preferred size first
  if (preferredSize !== 'full' && imageSource.thumbnails) {
    const thumbnail = imageSource.thumbnails[preferredSize];
    if (thumbnail) {
      // Try preferred format
      if (thumbnail[preferredFormat]) {
        return resolveImageUrl(thumbnail[preferredFormat]);
      }
      // Fallback to JPEG if enabled
      if (fallbackToJpeg && thumbnail.jpeg) {
        return resolveImageUrl(thumbnail.jpeg);
      }
    }
  }

  // Try main image with preferred format
  if (imageSource[preferredFormat]) {
    return resolveImageUrl(imageSource[preferredFormat]);
  }

  // Fallback to JPEG
  if (fallbackToJpeg && imageSource.jpeg) {
    return resolveImageUrl(imageSource.jpeg);
  }

  // Final fallback
  return getFallbackImageUrl();
};

/**
 * NEW: Construct Firebase Storage URL from path components
 * This is used when the client needs to construct URLs based on naming conventions
 */
export const constructFirebaseStorageUrl = (
  contentHash: string,
  type: 'main' | 'thumbnail',
  format: 'avif' | 'webp' | 'jpeg',
  size?: 'small' | 'medium' | 'large'
): string => {
  const basePath = 'uploads/processed';
  let fullPath: string;

  if (type === 'main') {
    fullPath = `${basePath}/${contentHash}/main/image.${format}`;
  } else {
    if (!size) {
      throw new Error('Size is required for thumbnail type');
    }
    fullPath = `${basePath}/${contentHash}/thumbnails/${size}/image.${format}`;
  }

  // This would need to be updated with actual Firebase Storage bucket URL
  // For now, return a placeholder that the server can resolve
  return `/api/storage/resolve/${encodeURIComponent(fullPath)}`;
};

/**
 * NEW: Parse Firebase Storage URL to extract metadata
 */
export const parseFirebaseStorageUrl = (
  url: string
): {
  isFirebaseStorage: boolean;
  contentHash?: string;
  type?: 'main' | 'thumbnail';
  format?: 'avif' | 'webp' | 'jpeg';
  size?: 'small' | 'medium' | 'large';
} => {
  if (!isFirebaseStorageUrl(url)) {
    return { isFirebaseStorage: false };
  }

  // Extract path from Firebase Storage URL
  const urlObj = new URL(url);
  const pathParts = urlObj.pathname.split('/');

  // Look for processed image pattern: uploads/processed/{hash}/{type}/{file}
  const processedIndex = pathParts.indexOf('processed');
  if (processedIndex === -1 || pathParts.length < processedIndex + 4) {
    return { isFirebaseStorage: true };
  }

  const contentHash = pathParts[processedIndex + 1];
  const type = pathParts[processedIndex + 2] as 'main' | 'thumbnail';
  const fileName = pathParts[pathParts.length - 1];

  // Extract format from filename
  const formatMatch = fileName.match(/\.(\w+)$/);
  const format = formatMatch ? (formatMatch[1] as 'avif' | 'webp' | 'jpeg') : undefined;

  // Extract size for thumbnails
  let size: 'small' | 'medium' | 'large' | undefined;
  if (type === 'thumbnail' && pathParts.length > processedIndex + 3) {
    size = pathParts[processedIndex + 3] as 'small' | 'medium' | 'large';
  }

  return {
    isFirebaseStorage: true,
    contentHash,
    type,
    format,
    size,
  };
};

/**
 * Get the fallback image URL when the primary image fails to load
 */
export const getFallbackImageUrl = (originalUrl?: string): string => {
  // Use brand logo as placeholder - SVG scales to any size
  return '/images/logos/deals-n-steals-logo-optimized.svg';
};
